/**
 * useCmsAutoRefresh - Hook for automatic CMS content updates
 * 
 * Polls cms-meta every 30 seconds, and when content_version changes,
 * fetches new content and applies it without page reload.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { applyI18nOverrides } from './applyI18nOverrides';
import { useContentUpdater } from './ContentContext';

const POLL_INTERVAL_MS = 30000; // 30 seconds
const CMS_META_URL = '/.netlify/functions/cms-meta';
const CMS_CONTENT_URL = '/.netlify/functions/cms-content';

/**
 * Hook to automatically refresh CMS content
 * Call this once at the app level
 */
export function useCmsAutoRefresh() {
    const { i18n } = useTranslation();
    const { setMedia, setContentVersion } = useContentUpdater();
    const currentVersionRef = useRef(0);
    const isInitializedRef = useRef(false);

    /**
     * Fetch content version from CMS
     */
    const fetchMeta = useCallback(async () => {
        try {
            const response = await fetch(CMS_META_URL);
            if (!response.ok) {
                console.warn('[CMS] Failed to fetch meta:', response.status);
                return null;
            }
            return await response.json();
        } catch (error) {
            console.warn('[CMS] Meta fetch error:', error.message);
            return null;
        }
    }, []);

    /**
     * Fetch published content for a language
     */
    const fetchContent = useCallback(async (lang) => {
        try {
            const response = await fetch(`${CMS_CONTENT_URL}?lang=${lang}`);
            if (!response.ok) {
                console.warn('[CMS] Failed to fetch content:', response.status);
                return null;
            }
            return await response.json();
        } catch (error) {
            console.warn('[CMS] Content fetch error:', error.message);
            return null;
        }
    }, []);

    /**
     * Apply content updates
     */
    const applyUpdates = useCallback(async (newVersion) => {
        const lang = i18n.language || 'en';
        const content = await fetchContent(lang);

        if (!content) {
            return false;
        }

        const { translations = {}, media = {} } = content;

        // Apply i18n overrides
        const translationCount = applyI18nOverrides(lang, translations);

        // Update media context
        setMedia(media);
        setContentVersion(newVersion);
        currentVersionRef.current = newVersion;

        if (process.env.NODE_ENV !== 'production') {
            console.log(`[CMS] Applied overrides: translations=${translationCount} media=${Object.keys(media).length}`);
        }

        return true;
    }, [i18n.language, fetchContent, setMedia, setContentVersion]);

    /**
     * Check for updates and apply if needed
     */
    const checkForUpdates = useCallback(async () => {
        const meta = await fetchMeta();
        if (!meta) return;

        const { content_version: newVersion } = meta;
        const currentVersion = currentVersionRef.current;

        if (newVersion > currentVersion) {
            if (process.env.NODE_ENV !== 'production') {
                console.log(`[CMS] Meta version changed: ${currentVersion} → ${newVersion}`);
            }
            await applyUpdates(newVersion);
        }
    }, [fetchMeta, applyUpdates]);

    /**
     * Initial load and language change handler
     */
    const initializeContent = useCallback(async () => {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[CMS] Initializing content...');
        }

        const meta = await fetchMeta();
        if (meta) {
            currentVersionRef.current = meta.content_version || 0;
            setContentVersion(meta.content_version || 0);
            await applyUpdates(meta.content_version || 0);
        }

        isInitializedRef.current = true;
    }, [fetchMeta, applyUpdates, setContentVersion]);

    // Initialize on mount
    useEffect(() => {
        initializeContent();
    }, [initializeContent]);

    // Re-fetch content when language changes
    useEffect(() => {
        if (isInitializedRef.current) {
            applyUpdates(currentVersionRef.current);
        }
    }, [i18n.language, applyUpdates]);

    // Poll for updates
    useEffect(() => {
        const intervalId = setInterval(checkForUpdates, POLL_INTERVAL_MS);

        if (process.env.NODE_ENV !== 'production') {
            console.log(`[CMS] Auto-refresh started (interval: ${POLL_INTERVAL_MS / 1000}s)`);
        }

        return () => {
            clearInterval(intervalId);
            if (process.env.NODE_ENV !== 'production') {
                console.log('[CMS] Auto-refresh stopped');
            }
        };
    }, [checkForUpdates]);

    return {
        checkForUpdates,
        currentVersion: currentVersionRef.current,
    };
}

/**
 * Component to initialize CMS auto-refresh
 * Include this once in your app tree inside ContentProvider
 */
export function CmsAutoRefreshInitializer() {
    useCmsAutoRefresh();
    return null;
}

export default useCmsAutoRefresh;
